import {
    Component,
    Input,
    Output,
    EventEmitter,
    ViewChild,
    forwardRef,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    HostBinding,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { JobType } from '@intranet/modules/jobs/job.types';

// Value used when every job type has been unticked. No job type has this id,
// so filtering by it returns nothing.
export const NO_JOB_TYPES = -1;

// Multi-select job type dropdown. Same look as team-picker, with the "All"
// behaviour of the timer reports client filter: everything starts ticked,
// "All" ticks/unticks every job type, and single types can be unticked.
// The value is an array of jobTypeIds: [] means All (no filter) and
// [NO_JOB_TYPES] means none.
@Component({
    selector: 'job-type-picker',
    templateUrl: './job-type-picker.component.html',
    styleUrls: ['./job-type-picker.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => JobTypePickerComponent),
        multi: true,
    }],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobTypePickerComponent implements ControlValueAccessor, OnChanges {
    @Input() jobTypes: JobType[] = [];
    @Input() allowAll = true;
    @Input() allLabel = 'All Job Types';
    @Input() disabled = false;
    @Input() minDropdownWidth: number = 0;
    @Input() showApply = true;
    @Output() applySelection = new EventEmitter<number[]>();

    @ViewChild('trigger') trigger!: CdkOverlayOrigin;

    @HostBinding('class.disabled') get isDisabled() { return this.disabled; }

    dropdownOpen = false;
    dropUp = false;
    overlayWidth = 0;

    private _value: number[] = [];
    private _pendingValue: number[] | null = null;
    selectedLabel = '';

    private onChange: (v: any) => void = () => {};
    onTouched: () => void = () => {};

    constructor(private cdr: ChangeDetectorRef) {}

    // The job types list can arrive after the value is written, so refresh
    // the label once it does.
    ngOnChanges(changes: SimpleChanges) {
        if (changes.jobTypes) {
            this.updateSelectedLabel();
        }
    }

    get showClearButton(): boolean {
        return this.allowAll && this._value.length > 0;
    }

    writeValue(v: number[] | null) {
        this._value = Array.isArray(v) ? v : [];
        this.updateSelectedLabel();
        this.cdr.markForCheck();
    }

    registerOnChange(fn: any) { this.onChange = fn; }
    registerOnTouched(fn: any) { this.onTouched = fn; }

    toggleDropdown() {
        if (this.disabled) return;
        if (this.dropdownOpen) {
            this.closeDropdown();
        } else {
            this.openDropdown();
        }
    }

    private openDropdown() {
        if (!this.trigger) return;
        const rect = this.trigger.elementRef.nativeElement.getBoundingClientRect();
        this.overlayWidth = Math.max(rect.width, this.minDropdownWidth);
        this.dropUp = window.innerHeight - rect.bottom < window.innerHeight * 0.47;
        this.dropdownOpen = true;
        this._pendingValue = this.showApply ? this.toTicked(this._value) : null;
    }

    closeDropdown() {
        this.dropdownOpen = false;
        if (this.showApply) this._pendingValue = null;
    }

    private getAllIds(): number[] {
        return (this.jobTypes || []).map(t => t.jobTypeId);
    }

    // The value as the list of ticked job types: [] (All) ticks every one,
    // NO_JOB_TYPES ticks none.
    private toTicked(value: number[]): number[] {
        if (!value || value.length === 0) return this.getAllIds();
        if (value.includes(NO_JOB_TYPES)) return [];
        return [...value];
    }

    // The reverse of toTicked: every job type ticked is stored as [] (no
    // filter), nothing ticked as NO_JOB_TYPES.
    private fromTicked(ticked: number[]): number[] {
        // No job types loaded: nothing to filter by, so stay on All rather
        // than ending up filtering to nothing.
        if (this.getAllIds().length === 0) return [];
        if (this.isFullSelection(ticked)) return [];
        if (ticked.length === 0) return [NO_JOB_TYPES];
        return ticked;
    }

    private isFullSelection(ticked: number[]): boolean {
        const allIds = this.getAllIds();
        return allIds.length > 0 && allIds.every(id => ticked.includes(id));
    }

    private getTicked(): number[] {
        return (this.showApply && this.dropdownOpen && this._pendingValue)
            ? this._pendingValue
            : this.toTicked(this._value);
    }

    isAllSelected(): boolean {
        return this.isFullSelection(this.getTicked());
    }

    isJobTypeSelected(jobTypeId: number): boolean {
        return this.getTicked().includes(jobTypeId);
    }

    // "All" is a master toggle: ticks every job type, or unticks them all if
    // they're already all ticked.
    onAllClick(event: Event) {
        event.stopPropagation();
        this.onTouched();
        this.setTicked(this.isAllSelected() ? [] : this.getAllIds());
    }

    onJobTypeClick(jobTypeId: number, event: Event) {
        event.stopPropagation();
        this.onTouched();
        const set = new Set(this.getTicked());
        set.has(jobTypeId) ? set.delete(jobTypeId) : set.add(jobTypeId);
        this.setTicked(Array.from(set));
    }

    private setTicked(ticked: number[]) {
        if (this.showApply) {
            this._pendingValue = ticked;
        } else {
            this._value = this.fromTicked(ticked);
            this.onChange(this._value);
            this.updateSelectedLabel();
        }
        this.cdr.markForCheck();
    }

    onApplyClick() {
        this._value = this.fromTicked(this._pendingValue ?? this.toTicked(this._value));
        this.onChange(this._value);
        this.updateSelectedLabel();
        this.applySelection.emit(this._value);
        this.closeDropdown();
    }

    // The ✕ button: back to All
    clear() {
        this._value = [];
        this._pendingValue = this.showApply ? this.getAllIds() : null;
        this.onChange(this._value);
        this.updateSelectedLabel();
        if (this.showApply) this.applySelection.emit(this._value);
        this.cdr.markForCheck();
    }

    private updateSelectedLabel() {
        if (!this._value || this._value.length === 0) {
            this.selectedLabel = '';
            return;
        }
        if (this._value.includes(NO_JOB_TYPES)) {
            this.selectedLabel = 'No job types selected';
            return;
        }
        if (this._value.length === 1) {
            const type = (this.jobTypes || []).find(t => t.jobTypeId === this._value[0]);
            this.selectedLabel = type?.description || '';
            return;
        }
        this.selectedLabel = `${this._value.length} job types selected`;
    }
}
